import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import { Button, Card } from '@/design-system/primitives';
import { PageHeader } from '@/components/app-shell';
import {
  useMyReferral,
  useMyRedemptions,
  useRedeemReferralCredit,
} from '@/lib/api/hooks';
import type { ReferralRedemption } from '@/types/api';
import { MemberCodeCard } from '@/features/home/components/MemberCodeCard';

const MIN_REDEEM_CENTS = 500; // mirrors backend _MIN_REDEEM_CENTS

function dollars(cents: number): string {
  const v = cents / 100;
  return v % 1 === 0 ? `$${v.toFixed(0)}` : `$${v.toFixed(2)}`;
}

/**
 * Patient-facing Referral Program page (/app/referrals, gated behind the
 * `gift-referrals` experiment).
 *
 * Code + share link, store-credit balance, referral stats, and the Redeem
 * flow. Redeemed credit becomes Stripe account credit that auto-applies to the
 * patient's next invoice (renewal or new order) — a single pool, so there's no
 * per-target picker (Stripe customer balance can't be reserved per invoice).
 */
export function ReferralProgramScreen() {
  // Referrals rolled out to 100% (gift-referrals experiment completed
  // 2026-07-13, variant-a won) — no cohort redirect anymore.
  const { data: referral, isLoading } = useMyReferral(true);
  return (
    <div className="flex flex-col">
      <PageHeader
        title="Referral Program"
        description="Share your code — your friend saves $20 on their first order, and you earn $20 for every treatment that ships."
      />
      <div className="flex flex-col px-15 md:px-40 py-25 gap-20 max-w-[65rem] mx-auto w-full">
        {isLoading || !referral ? (
          <p className="type-body-sm text-ink-muted">Loading…</p>
        ) : (
          <>
            <MemberCodeCard referral={referral} surface="referral_page" />
            <StatsRow referral={referral} />
            {/* Redeem leads once there's something to redeem; before that the
                explainer comes first so a first-timer sees the deal, not an
                empty payout card. */}
            {referral.available_cents > 0 ? (
              <>
                <RedeemCard referral={referral} />
                <HowItWorks />
              </>
            ) : (
              <>
                <HowItWorks />
                <RedeemCard referral={referral} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatsRow({
  referral,
}: {
  referral: NonNullable<ReturnType<typeof useMyReferral>['data']>;
}) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
      <Stat label="Available credit" value={dollars(referral.available_cents)} emphasis />
      <Stat label="Friends who ordered" value={String(referral.referrals_sent)} />
      <Stat label="Orders shipped" value={String(referral.orders_shipped)} />
      <Stat label="Earned total" value={dollars(referral.credit_earned_cents)} />
    </div>
  );
}

function Stat({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={`border border-line-subtle px-15 py-12 flex flex-col h-full ${emphasis ? 'bg-[var(--surface-sunken)]' : ''}`}
    >
      <div className="type-label-sm text-ink-muted uppercase tracking-tight">
        {label}
      </div>
      {/* mt-auto pins the value to the bottom so all four cards align even when
          some labels wrap to two lines. */}
      <div className={`mt-auto pt-8 ${emphasis ? 'type-title-lg' : 'type-title-md'}`}>
        {value}
      </div>
    </div>
  );
}

function RedeemCard({
  referral,
}: {
  referral: NonNullable<ReturnType<typeof useMyReferral>['data']>;
}) {
  const [open, setOpen] = useState(false);
  const { data: redemptions } = useMyRedemptions();
  const available = referral.available_cents;
  const hasHistory =
    (redemptions?.length ?? 0) > 0 || referral.redeemed_cents > 0;

  return (
    <Card padding="lg">
      <Card.Header>
        <Card.Title>Redeem credit</Card.Title>
      </Card.Header>
      <Card.Body>
        {available < MIN_REDEEM_CENTS ? (
          <p className="type-body-sm text-ink-muted">
            {available > 0
              ? `You have ${dollars(available)} in credit — you can redeem once you reach $5.`
              : hasHistory
                ? "You've redeemed all your available credit — you'll earn more as your referred friends' orders ship."
                : 'Refer a friend to start earning credit. It shows up here, ready to redeem against your next order or renewal.'}
          </p>
        ) : !open ? (
          <div className="flex items-center justify-between gap-15 flex-wrap">
            <p className="type-body-sm text-ink-secondary">
              You have <strong>{dollars(available)}</strong> ready to use.
            </p>
            <Button size="sm" onClick={() => setOpen(true)}>
              Redeem credit
            </Button>
          </div>
        ) : (
          <RedeemPanel referral={referral} onClose={() => setOpen(false)} />
        )}
        <RecentRedemptions redemptions={redemptions ?? []} />
      </Card.Body>
    </Card>
  );
}

function RedeemPanel({
  referral,
  onClose,
}: {
  referral: NonNullable<ReturnType<typeof useMyReferral>['data']>;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const redeem = useRedeemReferralCredit();
  const available = referral.available_cents;

  const [amountStr, setAmountStr] = useState((available / 100).toFixed(0));
  const [idemKey] = useState(() =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `redeem-${available}`,
  );
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const amountCents = Math.round((parseFloat(amountStr) || 0) * 100);
  const amountValid =
    amountCents >= MIN_REDEEM_CENTS && amountCents <= available;

  async function onConfirm() {
    setError(null);
    if (!amountValid) {
      setError(
        amountCents < MIN_REDEEM_CENTS
          ? 'The minimum redemption is $5.'
          : `That's more than your ${dollars(available)} available.`,
      );
      return;
    }
    try {
      await redeem.mutateAsync({
        amount_cents: amountCents,
        idempotency_key: idemKey,
      });
      setDone(true);
    } catch (e) {
      const msg =
        (e as { body?: { detail?: { message?: string } } })?.body?.detail
          ?.message || String((e as Error)?.message || e);
      setError(msg);
    }
  }

  if (done) {
    return (
      <div className="bg-status-success-subtle px-15 py-15">
        <p className="type-body-sm text-status-success">
          Added to your account credit.
        </p>
        <p className="type-label-sm text-ink-muted mt-6">
          It comes off your next order or renewal automatically — whichever’s
          first.
        </p>
        <div className="mt-12 flex gap-8">
          <Button size="sm" variant="secondary" onClick={onClose}>
            Done
          </Button>
          <Button size="sm" variant="ghost" onClick={() => navigate('/app/shop')}>
            Browse shop
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-15">
      <div>
        <label className="type-label-sm text-ink-muted uppercase tracking-tight">
          Amount to redeem
        </label>
        <div className="mt-6 flex items-center gap-10 flex-wrap">
          <div className="flex items-center border border-line-default">
            <span className="px-10 type-body-md text-ink-muted">$</span>
            <input
              type="number"
              min={MIN_REDEEM_CENTS / 100}
              max={available / 100}
              step={5}
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              className="w-90 px-6 py-8 type-body-md outline-none"
            />
          </div>
          <div className="flex gap-6">
            {[5, 10, 20].map((d) =>
              d * 100 <= available ? (
                <button
                  key={d}
                  type="button"
                  onClick={() => setAmountStr(String(d))}
                  className="px-10 py-6 border border-line-subtle type-label-sm hover:bg-[var(--surface-sunken)]"
                >
                  ${d}
                </button>
              ) : null,
            )}
            <button
              type="button"
              onClick={() => setAmountStr((available / 100).toFixed(0))}
              className="px-10 py-6 border border-line-subtle type-label-sm hover:bg-[var(--surface-sunken)]"
            >
              Max ({dollars(available)})
            </button>
          </div>
        </div>
      </div>

      <p className="type-body-sm text-ink-secondary bg-[var(--surface-sunken)] px-15 py-12">
        Credit is added to your account and automatically comes off your{' '}
        <strong>next order or renewal</strong> — whichever happens first.
      </p>

      {error && <p className="type-body-sm text-status-danger">{error}</p>}

      <div className="flex gap-8">
        <Button
          size="sm"
          onClick={onConfirm}
          loading={redeem.isPending}
          disabled={!amountValid}
        >
          Redeem {dollars(amountCents)}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Never mind
        </Button>
      </div>
    </div>
  );
}

function RecentRedemptions({
  redemptions,
}: {
  redemptions: ReferralRedemption[];
}) {
  const [showAll, setShowAll] = useState(false);
  if (redemptions.length === 0) return null;
  const shown = showAll ? redemptions : redemptions.slice(0, 3);
  const totalRedeemed = redemptions.reduce((acc, r) => acc + r.amount_cents, 0);
  return (
    <div className="mt-15 pt-15 border-t border-line-subtle">
      <div className="flex items-baseline justify-between gap-10 mb-8">
        <span className="type-label-sm text-ink-muted uppercase tracking-tight">
          Recent redemptions
        </span>
        <span className="type-label-sm text-ink-muted">
          Total redeemed:{' '}
          <span className="text-ink">{dollars(totalRedeemed)}</span>
        </span>
      </div>
      <ul className="flex flex-col">
        {shown.map((r) => (
          <li
            key={r.id}
            className="flex items-center justify-between py-8 border-b border-line-subtle last:border-b-0 type-body-sm"
          >
            <span className="flex items-center gap-10">
              <strong className="w-[3rem] shrink-0">
                {dollars(r.amount_cents)}
              </strong>
              <span
                className={`type-label-sm uppercase tracking-tight px-6 py-2 ${
                  r.applied
                    ? 'bg-status-success-subtle text-status-success'
                    : 'bg-[var(--surface-sunken)] text-ink-muted'
                }`}
                title={
                  r.applied
                    ? 'Applied to an order or renewal'
                    : 'In account credit — applies to your next order or renewal'
                }
              >
                {r.applied ? 'Applied' : 'Pending'}
              </span>
            </span>
            <span className="type-label-sm text-ink-muted">
              {r.created_at ? format(parseISO(r.created_at), 'MMM d') : ''}
            </span>
          </li>
        ))}
      </ul>
      {redemptions.length > 3 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-8 type-label-sm text-ink underline underline-offset-4"
        >
          {showAll ? 'Show less' : `See all ${redemptions.length}`}
        </button>
      )}
    </div>
  );
}

function HowItWorks() {
  return (
    <Card padding="lg">
      <Card.Header>
        <Card.Title>How it works</Card.Title>
      </Card.Header>
      <Card.Body>
        <ol className="flex flex-col gap-10 type-body-sm text-ink-secondary list-decimal pl-15">
          <li>Share your link or code with a friend</li>
          <li>They get $20 off of their first order</li>
          <li>
            You earn $20 for each of their orders that ships — redeem it here
            and it comes off of your next order or renewal
          </li>
        </ol>
      </Card.Body>
    </Card>
  );
}
